import { Routes, Route, Navigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// Import pages
import LoginPage from '@/pages/LoginPage';
import DashboardPage from '@/pages/DashboardPage';
import CalendarPage from '@/pages/CalendarPage';
import MapPage from '@/pages/MapPage';
import TicketsPage from '@/pages/TicketsPage';
import InventoryPage from '@/pages/InventoryPage';
import AdminPage from '@/pages/AdminPage';
import DevicesPage from '@/pages/DevicesPage';
import SchedulePage from '@/pages/SchedulePage';
import PortalPage from '@/pages/PortalPage';
import UsersPage from '@/pages/UsersPage';
import CompliancePage from '@/pages/CompliancePage';
import TechInboxPage from '@/pages/TechInboxPage';   // tech@customer.concept.bz inbox (2026-09-04)
import InternetPurchasesPage from '@/pages/InternetPurchasesPage';   // online software payments, apart from field work (2026-10-17)

// Mobile tech app (PWA)
import TechLayout from '@/pages/tech/TechLayout';
import TechMyDayPage from '@/pages/tech/TechMyDayPage';
import TechTicketPage from '@/pages/tech/TechTicketPage';
import TechHome from '@/pages/tech/TechHome';   // multi-role: lands on the active role's first function (2026-09-04)

// Layout component
import MainLayout from '@/components/layout/MainLayout';

// Auth context
import ProblemNotice from '@/components/ProblemNotice';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { WorkspaceProvider } from '@/contexts/WorkspaceContext';   // multi-role workspace (2026-09-04)

// Data sync context
import { DataSyncProvider } from '@/contexts/DataSyncContext';
import { WebSocketProvider } from '@/contexts/WebSocketContext';

// Protected Route wrapper
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #1E4A6B, #0D2233)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route wrapper (redirect home if already logged in — the home
// redirect then routes techs on phones to /tech, everyone else to /dashboard)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #1E4A6B, #0D2233)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

// Home: on small screens EVERY role gets the mobile app (/tech) — since 2026-09-04 it carries every function the
// user is authorized for (tickets, schedule, inbox, admin, ...) and adapts to the role they are working in.
// Desktop-width screens keep the classic dashboard.
const HomeRedirect = () => {
  const { user, isLoading } = useAuth();
  // Race fix 2026-07-08: never decide before auth has finished loading —
  // evaluating isTechnician(null) mis-routed phone techs to /dashboard.
  if (isLoading || !user) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #1E4A6B, #0D2233)',
        }}
      >
        <CircularProgress sx={{ color: 'white' }} size={60} />
      </Box>
    );
  }
  const smallScreen =
    typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches;
  if (smallScreen) {
    return <Navigate to="/tech" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route path="/portal" element={<PortalPage />} />

        {/* Mobile tech app (PWA) — own lightweight layout, no sidebar */}
        <Route
          path="/tech"
          element={
            <ProtectedRoute>
              <TechLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<TechHome />} />
          <Route path="my-day" element={<TechMyDayPage />} />
          <Route path="ticket/:id" element={<TechTicketPage />} />
          {/* every other authorized function renders inside the app shell (multi-role, 2026-09-04) */}
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="inbox" element={<TechInboxPage />} />
          <Route path="team" element={<UsersPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="compliance" element={<CompliancePage />} />
          <Route path="admin" element={<AdminPage />} />
        </Route>

        {/* Protected routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomeRedirect />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="schedule" element={<SchedulePage />} />
          <Route path="calendar" element={<CalendarPage />} />
          <Route path="map" element={<MapPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="inventory" element={<InventoryPage />} />
          <Route path="admin" element={<AdminPage />} />
          <Route path="devices" element={<DevicesPage />} />
          <Route path="team" element={<UsersPage />} />
          <Route path="compliance" element={<CompliancePage />} />
          <Route path="tech-inbox" element={<TechInboxPage />} />
          <Route path="internet-purchases" element={<InternetPurchasesPage />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Box>
  );
}

function App() {
  return (
    <AuthProvider>
      <WorkspaceProvider>
      <DataSyncProvider>
        <WebSocketProvider>
        <AppRoutes />
        <ProblemNotice />
        </WebSocketProvider>
      </DataSyncProvider>
      </WorkspaceProvider>
    </AuthProvider>
  );
}

export default App;
